/**
 *
 * OrderForm
 *
 */

import { setOrderInProcess } from "containers/App/actions";
import { addYears, getYear } from "date-fns";
import { bool, func, object, string, oneOfType, number } from "prop-types";
import React from "react";
import { connect } from "react-redux";
import Loader from "react-loader-advanced";
import { ThreeBounce } from "better-react-spinkit";
import {
  BILLING_INFO_INPUT_REQUIREMENTS_LIBRARY,
  ACCOUNT_SUBSIDIARY_LIBRARY,
} from "utils/shared";
import { trackCheckoutFlowClick } from "utils/trackingFunctions";
import { getScheduleDemoUrl } from "components/VideoInterviewMerchandizingModal/helperFunctions";
import { freeTrialSelfServicePurchaseUtmParams } from "utils/helperConstants";
import OrderPage from "./OrderPage";
import PaymentPage from "./PaymentPage";
import LoadingPage from "./LoadingPage";
import ErrorPage from "./ErrorPage";
import ConfirmationPage from "./ConfirmationPage";
import { fetchTaxData, getCreditCardType } from "./helperFunctions";
import Header from "./Header";

const PAYMENT_PAGE_PATHS = {
  versionUpgrade: "/version_upgrade_payment",
  addZipRecruiter: "/add_ziprecruiter_payment",
  subscription: "/payment",
  zipRecruiterUpgrade: "/ziprecruiter_upgrade_payment",
  invoice: "/paymentPage",
};

class OrderForm extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      companyName: "",
      firstName: "",
      lastName: "",
      emailAddress: "",
      jobTitle: "",
      paymentMethod: "Credit",
      cardNumber: "",
      addressStreet: "",
      addressCity: "",
      addressState: "",
      addressZip: "",
      addressCountry: "",
      telephoneNumber: "",
      agreeToTerms: false,
      contractPrice: "",
      fullUpgradePrice: "",
      contractMonths: "",
      taxAmount: "",
      paymentInformationError: false,
      orderPageValidated: false,
      paymentPageValidated: false,
      stateError: "",
      invalidCCInput: false,
      isAutoRenewalEnabled: true,
      taxLoadingStatus: "idle",
      proceedToPaymentClicked: false,
      showOrderDescription: false,
      showRingFencing: false,
      showParameter: false,
      currencyCode: "",
      isLoadingTimeOut: false,
      sfRecordType: "",
      subsidiary: "",
    };
  }

  /* ----------- React LifeCycle Methods ----------- */

  UNSAFE_componentWillMount() {
    if (this.props.billingInfoFromInvoicePage) {
      this.setState({
        companyName: this.props.billingInfoFromInvoicePage.companyName,
        firstName: this.props.billingInfoFromInvoicePage.firstName,
        lastName: this.props.billingInfoFromInvoicePage.lastName,
        jobTitle: this.props.billingInfoFromInvoicePage.jobTitle,
        emailAddress: this.props.billingInfoFromInvoicePage.emailAddress,
        telephoneNumber: this.props.billingInfoFromInvoicePage.telephoneNumber,
        addressStreet: this.props.billingInfoFromInvoicePage.addressStreet,
        addressCity: this.props.billingInfoFromInvoicePage.addressCity,
        addressState:
          this.props.billingInfoFromInvoicePage.addressState === ""
            ? this.props.billingInfoFromInvoicePage.addressState
            : "XX",
        addressZip: this.props.billingInfoFromInvoicePage.addressZip,
        addressCountry: this.props.billingInfoFromInvoicePage.addressCountry,
        contractPrice: this.props.pricing.price,
        contractMonths: this.props.pricing.months,
        taxAmount: this.props.billingInfoFromInvoicePage.taxAmount,
        subsidiary: this.props.billingInfoFromInvoicePage.subsidiary,
      });
    }

    if (this.props.billingInfo && this.props.billingInfo.size > 0) {
      this.setState({
        companyName: this.props.billingInfo.get("companyName") || "",
        firstName: this.props.billingInfo.get("firstName") || "",
        lastName: this.props.billingInfo.get("lastName") || "",
        emailAddress: this.props.billingInfo.get("emailAddress") || "",
        jobTitle: this.props.billingInfo.get("jobTitle") || "",
        addressStreet: this.props.billingInfo.get("addressStreet") || "",
        addressCity: this.props.billingInfo.get("addressCity") || "",
        addressState: this.props.billingInfo.get("addressState") || "XX",
        addressZip: this.props.billingInfo.get("addressZip") || "",
        addressCountry: this.props.billingInfo.get("addressCountry") || "",
        telephoneNumber: this.props.billingInfo.get("telephoneNumber") || "",
        currencyCode:
          this.props.billingInfo.get("currencyCode") &&
          this.props.callLocation !== PAYMENT_PAGE_PATHS.invoice
            ? this.props.billingInfo.get("currencyCode")
            : "",
        subsidiary: this.props.billingInfo.get("subsidiary"),
      });
    }

    if (this.props.pricing && this.props.pricing.size > 0) {
      let { taxAmount } = this.state;
      if (
        this.props.billingInfo.get("subsidiary") &&
        ACCOUNT_SUBSIDIARY_LIBRARY[this.props.billingInfo.get("subsidiary")] ===
          "AU"
      ) {
        taxAmount = this.props.pricing.get("apacSalesTaxAmount") || taxAmount;
      }

      if (this.props.callLocation !== PAYMENT_PAGE_PATHS.invoice) {
        if (
          this.props.pricing.get("descriptions") &&
          this.props.pricing.get("descriptions").size > 0
        ) {
          this.setState({ showOrderDescription: true });
        }

        if (
          this.props.pricing.get("ringFencing") &&
          this.props.pricing.get("ringFencing") !== null
        ) {
          this.setState({ showRingFencing: true });
        }

        if (
          this.props.pricing.get("parameter") &&
          this.props.pricing.get("parameter") !== null
        ) {
          this.setState({ showParameter: true });
        }
      }

      // this sets the price that gets passed down
      if (
        this.props.isBuyNowEligible &&
        this.props.pricing.get("price") === null
      ) {
        // buyNowEligible accounts that do not have a quote created yet
        // will see contract price USD 3500 / AUD 5250 on Order Info page, depending on the currencyCode
        let contractPrice = 0;
        if (this.props.billingInfo.get("currencyCode") === "USD") {
          contractPrice = 3500;
        }
        if (this.props.billingInfo.get("currencyCode") === "AUD") {
          contractPrice = 5250;
        }

        this.setState({
          contractPrice,
        });
      } else if (
        ["zipRecruiterPurchase", "zipRecruiterPurchasePayment"].includes(
          this.props.callLocation
        )
      ) {
        this.setState({
          contractPrice: this.props.pricing.get("totalPrice"),
        });
      } else {
        this.setState({
          contractPrice: this.props.pricing.get("price"),
          contractMonths: this.props.pricing.get("months"),
          taxAmount,
          sfRecordType: this.props.pricing.get("sfRecordType"),
        });
      }
    } else if (
      ["orderPage", PAYMENT_PAGE_PATHS.invoice].includes(
        this.props.callLocation
      ) &&
      this.props.pricing &&
      this.props.pricing.length > 0
    ) {
      this.setState({
        contractPrice: this.props.pricing.price,
        contractMonths: this.props.pricing.months,
      });
    }

    if (this.props.companyInfo) {
      this.setState({
        isAutoRenewalEnabled: Boolean(
          this.props.companyInfo.get("autoRenewalEnabled")
        ),
      });
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      nextProps.billingInfo &&
      nextProps.billingInfo.size > 0 &&
      this.props.billingInfo !== nextProps.billingInfo
    ) {
      this.setState({
        companyName: nextProps.billingInfo.get("companyName") || "",
        firstName: nextProps.billingInfo.get("firstName") || "",
        lastName: nextProps.billingInfo.get("lastName") || "",
        emailAddress: nextProps.billingInfo.get("emailAddress") || "",
        jobTitle: nextProps.billingInfo.get("jobTitle") || "",
        addressStreet: nextProps.billingInfo.get("addressStreet") || "",
        addressCity: nextProps.billingInfo.get("addressCity") || "",
        addressState: nextProps.billingInfo.get("addressState") || "XX",
        addressZip: nextProps.billingInfo.get("addressZip") || "",
        addressCountry: nextProps.billingInfo.get("addressCountry") || "",
        telephoneNumber: nextProps.billingInfo.get("telephoneNumber") || "",
        currencyCode:
          nextProps.billingInfo.get("currencyCode") &&
          this.props.callLocation !== PAYMENT_PAGE_PATHS.invoice
            ? nextProps.billingInfo.get("currencyCode")
            : "",
        subsidiary: nextProps.billingInfo.get("subsidiary"),
      });
    }

    if (
      ["zipRecruiterUpgrade", "zipRecruiterAddPayment"].includes(
        nextProps.callLocation
      )
    ) {
      this.setState({
        fullUpgradePrice: nextProps.pricing.first().get("regularPrice"),
        contractPrice: nextProps.pricing.first().get("upgradePrice"),
        defaultZRCredits: nextProps.pricing.first().get("defaultZRCredits"),
      });
    }

    if (this.props.companyInfo !== nextProps.companyInfo) {
      this.setState({
        isAutoRenewalEnabled: Boolean(
          nextProps.companyInfo.get("autoRenewalEnabled")
        ),
      });
    }
  }

  // account for the case where the user clicks the proceed to payment button and the buy now quote is still loading
  // once the buy now quote is successful, fetch the tax data
  componentDidUpdate(prevProps) {
    if (
      this.state.proceedToPaymentClicked &&
      prevProps.buyNowQuoteLoadingStatus !==
        this.props.buyNowQuoteLoadingStatus &&
      this.props.buyNowQuoteLoadingStatus === "success"
    ) {
      this.handleDelayedFetchTaxData();
    }
  }

  handleDelayedFetchTaxData = () => {
    this.setState({
      taxLoadingStatus: "loading",
    });
    this.handleFetchTaxData();
  };

  /* ----------- Validation Methods ----------- */

  updateOrderPageValidation = (orderPageValidated) => {
    this.setState({
      orderPageValidated,
    });
  };

  updatePaymentPageValidation = (paymentPageValidated) => {
    this.setState({ paymentPageValidated });
  };

  /* ----------- Event Handlers ------------ */

  handleInput = (event) => {
    const { id: field, value } = event.target;

    // check if the addressState field is the one being changed, then we need to reset the taxAmount,
    // since it may have been previously set.
    if (field === "addressState" && !this.props.billingInfoFromInvoicePage) {
      const isNASubsidiary =
        ACCOUNT_SUBSIDIARY_LIBRARY[this.props.billingInfo.get("subsidiary")] ===
        "NA";
      if (isNASubsidiary || this.props.platformInstance === "NA") {
        this.setState({ taxAmount: "" });
      }
    } else if (field === "card-number") {
      this.setState({
        cardNumber: parseInt(value.replace(/ /g, ""), 10), // remove the spaces and make it a number
        invalidCCInput: false, // reset the error on new input
      });
      return;
    } else if (field === "card-expiry") {
      this.setState({
        fullExpDate: value,
        invalidCCInput: false, // reset the error on new input
      });
      return;
    } else if (field === "cvc") {
      this.setState({ invalidCCInput: false }); // reset the error on new input
    }

    if (
      field === "addressCountry" &&
      ![...BILLING_INFO_INPUT_REQUIREMENTS_LIBRARY.state].includes(value) &&
      value !== ""
    ) {
      this.setState({ addressState: "XX" });
    }

    this.setState({ [field]: value });
  };

  handleRadio = (event) => {
    this.setState({ paymentMethod: event.target.id });
  };

  toggleCheckbox = () => {
    this.setState((prevState) => ({
      agreeToTerms: !prevState.agreeToTerms,
    }));
  };

  handleUpdateAutoRenewal = () => {
    this.setState((prevState) => ({
      isAutoRenewalEnabled: !prevState.isAutoRenewalEnabled,
    }));
  };

  handleFetchTaxData = async () => {
    try {
      const formData = {
        firstName: this.state.firstName,
        lastName: this.state.lastName,
        telephoneNumber: this.state.telephoneNumber,
        jobTitle: this.state.jobTitle,
        emailAddress: this.state.emailAddress,
        addressStreet: this.state.addressStreet,
        addressCity: this.state.addressCity,
        addressState: this.state.addressState,
        addressZip: this.state.addressZip,
        addressCountry: this.state.addressCountry,
        isInternal: Boolean(
          this.props.userData && this.props.userData.internal > 0
        ),
        isBuyNowQuote:
          this.props.isBuyNowEligible ||
          this.state.sfRecordType === "Buy Now Quote",
      };

      const data = await fetchTaxData(formData);

      if (!data) {
        this.setState({
          paymentInformationError: true,
          taxLoadingStatus: "error",
        });
      }

      const { callLocation, updatePageToShow } = this.props;
      const { pricing, billing, tax } = data;
      const {
        price,
        months,
        sfRecordType,
        descriptions,
        ringFencing,
        parameter,
      } = pricing;
      const { subsidiary } = billing;

      // if buy now quote flow, update these state values based on the quote details returned
      const stateToUpdateForBuyNowQuoteFlow = this.props.isBuyNowEligible
        ? {
            contractPrice: price,
            contractMonths: months,
            sfRecordType,
            subsidiary,
            showOrderDescription:
              callLocation !== PAYMENT_PAGE_PATHS.invoice &&
              descriptions &&
              descriptions.length > 0,
            showRingFencing:
              callLocation !== PAYMENT_PAGE_PATHS.invoice &&
              ringFencing &&
              ringFencing !== null,
            showParameter:
              callLocation !== PAYMENT_PAGE_PATHS.invoice &&
              parameter &&
              parameter !== null,
          }
        : {};

      const stateToUpdate = {
        taxAmount: tax || "",
        paymentInformationError: false,
        taxLoadingStatus: "success",
        ...stateToUpdateForBuyNowQuoteFlow,
      };

      this.setState(stateToUpdate);

      switch (callLocation) {
        case "versionUpgrade":
          updatePageToShow(PAYMENT_PAGE_PATHS.versionUpgrade);
          break;
        case "zipRecruiterUpgrade":
          updatePageToShow(PAYMENT_PAGE_PATHS.addZipRecruiter);
          break;
        case "subscribe":
        case "payment":
          updatePageToShow(PAYMENT_PAGE_PATHS.subscription);
          break;
        default:
          updatePageToShow(PAYMENT_PAGE_PATHS.zipRecruiterUpgrade);
          break;
      }
    } catch (error) {
      this.setState({
        paymentInformationError: true,
        taxLoadingStatus: "error",
      });
      // eslint-disable-next-line no-console
      console.error("Error fetching tax data:", error);
    }
  };

  handleProceedToPaymentButton = () => {
    const isSelectedCountryRequiredState =
      BILLING_INFO_INPUT_REQUIREMENTS_LIBRARY.state.includes(
        this.state.addressCountry
      );

    const isStateInputValid =
      (isSelectedCountryRequiredState &&
        this.state.addressState.length > 0 &&
        this.state.addressState !== "XX") ||
      !isSelectedCountryRequiredState;

    if (isStateInputValid) {
      trackCheckoutFlowClick(
        "Continue to Payment Details click",
        "Order-Info-Page"
      );

      this.setState({
        proceedToPaymentClicked: true,
      });

      // if the user clicks proceed to payment before the buy now quote is loaded,
      // we need to wait for the buy now quote to finish loading first before fetching the tax data
      if (
        this.props.isBuyNowEligible &&
        this.props.buyNowQuoteLoadingStatus !== "success"
      ) {
        return;
      }

      if (this.props.callLocation === "subscribe") {
        this.setState({
          taxLoadingStatus: "loading",
        });
        this.handleFetchTaxData();
      } else if (this.props.callLocation === "orderPage") {
        this.props.updatePageToShow(PAYMENT_PAGE_PATHS.invoice);
      }
    } else {
      const stateError = "Please select a state within the U.S. or Australia";
      this.setState({ stateError });
    }
  };

  shouldHideInvoice = () =>
    // if the invoice has already been paid
    (this.props.billingInfo.get("paid") === 0 &&
      this.props.billingInfo.get("salesHistoryId") !== "") ||
    // if payment plan is turned on
    this.props.billingInfo.get("allowPaymentPlan") === 1 ||
    // if Credit Card only is enabled for the company
    this.props.billingInfo.get("creditCardOnly") ||
    // if the user is suspended
    (this.props.userData && this.props.userData.suspended) ||
    // if the user is buying ZipRecruiter credits
    this.props.callLocation === "zipRecruiterPurchasePayment" ||
    // if the user is checking out the Buy Now Quote
    this.state.sfRecordType === "Buy Now Quote";

  handleCCInputError = () => {
    this.setState({ invalidCCInput: true });
  };

  handleOrderButton = () => {
    if (this.state.creditCardInvalid) {
      this.setState({ creditCardInvalid: false });
    }

    /*
     * Check if the loginToken has an invoice number.  If it does, the candidate is paying the invoice by credit
     * card now, so we add the invoice number to the formData, and will be using a different endpoint.  If not,
     * we will be using the upgrade endpoint.
     */

    let invoiceNumber = null;
    if (
      this.props.userData &&
      this.props.userData.status === "subscriber" &&
      this.props.userData.daysRemaining > 60 &&
      this.props.userData.paid === 0
    ) {
      const { invoiceNumber: invoiceNumberFromUserData } = this.props.userData;
      invoiceNumber = invoiceNumberFromUserData;
    } else if (this.props.billingInfoFromInvoicePage) {
      // user is not required to log in if paying the invoice by credit card through the payment link (invoice-payment route)
      const { invoiceNumber: invoiceNumberFromInvoicePage } = this.props;
      invoiceNumber = invoiceNumberFromInvoicePage;
    }
    const expirationDateAndYear =
      this.state.paymentMethod === "Invoice"
        ? ["", ""]
        : this.state.fullExpDate.split(" / ");
    const cardType =
      this.state.paymentMethod === "Invoice"
        ? ""
        : getCreditCardType(this.state.cardNumber.toString()[0]);
    const formData = {
      companyName: this.state.companyName,
      companyAccountId: this.props.billingInfo.get("companyAccountId"),
      firstName: this.state.firstName,
      lastName: this.state.lastName,
      emailAddress: this.state.emailAddress,
      jobTitle: this.state.jobTitle,
      paymentMethod: this.state.paymentMethod,
      creditCardType: cardType,
      cardNumber: this.state.cardNumber,
      expirationMonth: expirationDateAndYear[0],
      expirationYear: `20${expirationDateAndYear[1]}`,
      cardCode: this.state.cvc,
      addressStreet: this.state.addressStreet,
      addressStreet2: this.props.billingInfo.get("addressStreet2"),
      addressCity: this.state.addressCity,
      addressState: this.state.addressState,
      addressZip: this.state.addressZip,
      addressCountry: this.state.addressCountry,
      telephoneNumber: this.state.telephoneNumber,
      faxNumber: this.props.billingInfo.get("faxNumber"),
      agreeToTerms: this.state.agreeToTerms,
      contractPrice: this.state.contractPrice,
      contractMonths: this.state.contractMonths,
      tax: this.state.taxAmount === "" ? 0 : this.state.taxAmount,
      amount: this.state.contractPrice,
      renewal: this.props.billingInfo.get("freeTrial") ? 0 : 1,
      hsVersion: this.props.billingInfo.get("hsVersion"),
      companyPricingId:
        this.props.callLocation === PAYMENT_PAGE_PATHS.invoice
          ? this.props.pricing.companyPricingId
          : this.props.pricing.get("companyPricingId"),
      defaultZRCredits: this.state.defaultZRCredits,
      invoiceNumber,
      autoRenewalEnabled: this.state.isAutoRenewalEnabled,
    };

    trackCheckoutFlowClick("Order now click", "Payment-Info-Page");
    this.props.onUpdateOrderInProcess();
    this.props.onHandlePurchaseOrUpgrade(formData);
  };

  render() {
    // Props to Variables
    const {
      billingInfo,
      billingInfoFromInvoicePage,
      billingInfoLoaded,
      callLocation,
      companyInfo,
      creditCardInvalid,
      errorMessage,
      orderInProcess,
      pricing,
      userData,
      dispatch,
      buyNowQuoteLoadingStatus,
      orderStatus,
      platformInstance,
      userStatusBeforeCheckout,
    } = this.props;

    const isAU = userData && userData.locale === "AU";

    const isAUSubsidiary =
      ACCOUNT_SUBSIDIARY_LIBRARY[this.state.subsidiary] === "AU";
    const isFreeTrial = userData && userData.status === "free_trial";

    if (this.state.proceedToPaymentClicked) {
      if (
        buyNowQuoteLoadingStatus === "error" ||
        this.state.taxLoadingStatus === "error" ||
        this.state.isLoadingTimeOut ||
        orderStatus === "error"
      ) {
        const demoLink = getScheduleDemoUrl(
          isFreeTrial,
          userData.rep,
          freeTrialSelfServicePurchaseUtmParams.checkoutError
        );

        return <ErrorPage dispatch={dispatch} demoLink={demoLink} />;
      }

      if (
        buyNowQuoteLoadingStatus === "loading" ||
        this.state.taxLoadingStatus === "loading"
      ) {
        return (
          <LoadingPage
            setIsLoadingTimeOutInParent={() => {
              this.setState({ isLoadingTimeOut: true });
            }}
          />
        );
      }

      if (orderStatus === "success") {
        return (
          <ConfirmationPage
            dispatch={dispatch}
            userData={userData}
            userStatusBeforeCheckout={userStatusBeforeCheckout}
          />
        );
      }
    }

    // show the auto renewal error message if
    // 1. There is a quote returned
    // 2. The account has auto renewal eligible and enabled
    // 3. The account is past due
    // 4. The account is subscriber
    const showAutoRenewalErrorMessage = Boolean(
      pricing &&
        pricing.size > 0 &&
        companyInfo.size > 0 &&
        companyInfo.get("autoRenewalEligibility") &&
        companyInfo.get("autoRenewalEnabled") &&
        userData &&
        userData.daysRemaining < 0 &&
        userData.status === "subscriber"
    );

    const showAutoRenewalToggle = Boolean(
      companyInfo.size > 0 &&
        companyInfo.get("autoRenewalEligibility") &&
        !companyInfo.get("autoRenewalEnabled") &&
        userData &&
        userData.status === "subscriber"
    );

    // internal users should not be able to update auto renewal
    const allowUpdateAutoRenewal = !(userData && userData.internal);

    // This text is intended to show Free Trials where autoRenewalEligibility is true
    // because Small Business Prospects are automatically opted in to auto-renewal
    const showAutoRenewalText =
      isFreeTrial &&
      Boolean(
        companyInfo.size > 0 && companyInfo.get("autoRenewalEligibility")
      );

    // Set up Expiration Years array
    const YearsArray = [];
    for (let i = 0; i < 10; i += 1) {
      YearsArray.push(
        <option key={i} value={getYear(addYears(new Date(), i))}>
          {getYear(addYears(new Date(), i))}
        </option>
      );
    }

    const showLoader =
      !billingInfoLoaded &&
      !billingInfoFromInvoicePage &&
      !userData &&
      !billingInfo;

    const showOrderPage = [
      "zipRecruiterPurchase",
      "versionUpgrade",
      "zipRecruiterUpgrade",
      "subscribe",
      "orderPage",
    ].includes(callLocation);

    const showPaymentPage = [
      "zipRecruiterPurchasePayment",
      "versionUpgradePayment",
      "zipRecruiterAddPayment",
      "payment",
      "/paymentPage",
      "invoicePayment",
    ].includes(callLocation);

    const title = showPaymentPage ? "Payment Info" : "Ordering Info";
    const showTitle = ["subscribe", "payment"].includes(callLocation);

    return (
      <Loader
        show={showLoader}
        message={<ThreeBounce color="#1F469A" size={50} />}
        contentBlur={30}
        messageStyle={{
          display: "flex",
          justifyContent: "center",
          marginTop: "200px",
        }}
        backgroundStyle={{ backgroundColor: "rgba( 0 0 0 0)" }}
      >
        {showTitle ? <Header title={title} /> : null}
        {showOrderPage ? (
          <OrderPage
            billingInfo={billingInfo}
            orderPageValidated={this.state.orderPageValidated}
            updateOrderPageValidation={this.updateOrderPageValidation}
            companyName={this.state.companyName}
            firstName={this.state.firstName}
            lastName={this.state.lastName}
            emailAddress={this.state.emailAddress}
            jobTitle={this.state.jobTitle}
            addressStreet={this.state.addressStreet}
            addressCity={this.state.addressCity}
            addressState={this.state.addressState}
            addressZip={this.state.addressZip}
            addressCountry={this.state.addressCountry}
            telephoneNumber={this.state.telephoneNumber}
            contractPrice={this.state.contractPrice}
            handleInput={this.handleInput}
            handleProceedToPaymentButton={this.handleProceedToPaymentButton}
            pricing={pricing}
            callLocation={callLocation}
            fullUpgradePrice={this.state.fullUpgradePrice}
            paymentInformationError={this.state.paymentInformationError}
            stateError={this.state.stateError}
            billingInfoFromInvoicePage={billingInfoFromInvoicePage}
            taxAmount={this.state.taxAmount}
            isAU={isAU}
            isAUSubsidiary={isAUSubsidiary}
            showAutoRenewalErrorMessage={showAutoRenewalErrorMessage}
            showOrderDescription={this.state.showOrderDescription}
            showRingFencing={this.state.showRingFencing}
            showParameter={this.state.showParameter}
            currencyCode={this.state.currencyCode}
            platformInstance={platformInstance}
          />
        ) : null}

        {showPaymentPage ? (
          <PaymentPage
            handleRadio={this.handleRadio}
            handleInput={this.handleInput}
            paymentMethod={this.state.paymentMethod}
            shouldHideInvoice={this.shouldHideInvoice}
            yearsArray={YearsArray}
            paymentPageValidated={this.state.paymentPageValidated}
            updatePaymentPageValidation={this.updatePaymentPageValidation}
            creditCardInvalid={creditCardInvalid}
            errorMessage={errorMessage}
            toggleCheckbox={this.toggleCheckbox}
            agreeToTerms={this.state.agreeToTerms}
            contractPrice={this.state.contractPrice}
            taxAmount={this.state.taxAmount}
            handleOrderButton={this.handleOrderButton}
            pricing={pricing}
            callLocation={callLocation}
            orderInProcess={orderInProcess}
            handleCCInputError={this.handleCCInputError}
            invalidCCInput={this.state.invalidCCInput}
            isAU={isAU}
            allowPaymentPlan={
              billingInfo.get("allowPaymentPlan") || this.state.allowPaymentPlan
            }
            showAutoRenewalToggle={showAutoRenewalToggle}
            allowUpdateAutoRenewal={allowUpdateAutoRenewal}
            handleUpdateAutoRenewal={this.handleUpdateAutoRenewal}
            isAutoRenewalEnabled={this.state.isAutoRenewalEnabled}
            isAUSubsidiary={isAUSubsidiary}
            showAutoRenewalErrorMessage={showAutoRenewalErrorMessage}
            showOrderDescription={this.state.showOrderDescription}
            showRingFencing={this.state.showRingFencing}
            showParameter={this.state.showParameter}
            currencyCode={this.state.currencyCode}
            showAutoRenewalText={showAutoRenewalText}
          />
        ) : null}
      </Loader>
    );
  }
}

OrderForm.propTypes = {
  billingInfo: object,
  billingInfoLoaded: bool,
  pricing: object,
  creditCardInvalid: oneOfType([bool, number]),
  errorMessage: string,
  onHandlePurchaseOrUpgrade: func,
  companyInfo: object.isRequired,
  callLocation: string,
  updatePageToShow: func.isRequired,
  orderInProcess: bool.isRequired,
  onUpdateOrderInProcess: func.isRequired,
  invoiceNumber: number,
  billingInfoFromInvoicePage: object,
  userData: object,
  dispatch: func.isRequired,
  isBuyNowEligible: bool,
  buyNowQuoteLoadingStatus: string,
  orderStatus: string,
  platformInstance: string,
  userStatusBeforeCheckout: string,
};

const mapStateToProps = (state) => ({
  companyInfo: state.get("App").get("companyInfo"),
  locations: state.get("route").get("locationBeforeTransitions"),
  userData: state.get("App").get("userData"),
});

function mapDispatchToProps(dispatch) {
  return {
    onUpdateOrderInProcess: () => dispatch(setOrderInProcess()),
    dispatch,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(OrderForm);
